/**
 * COLONNES POUR TABLE DES TÂCHES
 * Définition des colonnes pour la table Mes tâches
 */

import type { ColumnDef } from '@tanstack/react-table';
import { Phone, Mail, Calendar, FileText, Eye, Trash2 } from 'lucide-react';
import { TableCell } from '@/components/table';
import type { Task } from '@/types/task';
import { formatTaskDueDate } from '@/lib/date-utils';

export interface TaskWithEntity extends Task {
  entity_name?: string;
}

// Label du type de tâche
function getTaskTypeLabel(type: Task['type']) {
  switch (type) {
    case 'call':
      return 'Appel';
    case 'email':
      return 'Email';
    case 'meeting':
      return 'Réunion';
    case 'other':
      return 'Autre';
    default:
      return 'Autre';
  }
}

// Icône selon le type de tâche
function getTaskIcon(type: Task['type']) {
  const iconClass = 'size-4';
  switch (type) {
    case 'call':
      return <Phone className={iconClass} />;
    case 'email':
      return <Mail className={iconClass} />;
    case 'meeting':
      return <Calendar className={iconClass} />;
    case 'other':
      return <FileText className={iconClass} />;
    default:
      return <FileText className={iconClass} />;
  }
}

// Vérifier si une tâche est urgente (échéance dans moins de 3 jours)
function isUrgent(dueDate: string) {
  const threeDaysFromNow = new Date();
  threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
  return new Date(dueDate) <= threeDaysFromNow;
}

export function createTasksColumns(
  onView?: (task: Task) => void,
  onDelete?: (taskId: string) => void
): ColumnDef<TaskWithEntity>[] {
  return [
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ getValue }) => {
        const type = getValue() as Task['type'];
        return (
          <div className="flex items-center gap-2">
            {getTaskIcon(type)}
            <span className="text-sm text-zinc-900">{getTaskTypeLabel(type)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'title',
      header: 'Nom tâche',
      cell: ({ row }) => {
        const task = row.original;
        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-zinc-900">{task.title}</span>
            {task.notes && <span className="text-xs text-zinc-500 line-clamp-1">{task.notes}</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'entity_name',
      header: 'Concerné',
      cell: ({ getValue }) => {
        const entityName = getValue() as string | undefined;
        return <TableCell>{entityName || '-'}</TableCell>;
      },
    },
    {
      accessorFn: (row) => formatTaskDueDate(row.due_date),
      id: 'due_date',
      header: 'Date',
      cell: ({ getValue, row }) => {
        const urgent = isUrgent(row.original.due_date);
        return (
          <TableCell className={urgent ? 'text-red-600 font-medium' : ''}>
            {getValue() as string}
          </TableCell>
        );
      },
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const task = row.original;
        return (
          <div className="flex items-center justify-end w-full gap-1">
            {onView && (
              <button
                onClick={() => onView(task)}
                className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
                title="Consulter l'entité"
              >
                <Eye className="size-4 text-zinc-900" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(task.id)}
                className="flex items-center justify-center size-9 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-red-50 transition-colors"
                title="Supprimer"
              >
                <Trash2 className="size-4 text-zinc-900" />
              </button>
            )}
          </div>
        );
      },
      enableSorting: false,
      size: 100,
    },
  ];
}
